/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { EuiFieldSearch, EuiInputPopover, useEuiTheme } from '@elastic/eui'

import { searchableFields } from './constants'

import type { SearchableProjectField, SearchableProject } from './types'

const sortFunction = (a: string, b: string) =>
  a.localeCompare(b, undefined, {
    numeric: true,
    sensitivity: 'base',
  })

const camelCaseToTitleCase = (str: string): string => {
  // Handle special cases
  if (str === 'id') {
    return 'ID'
  }

  // Convert camelCase to Title Case
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (char) => char.toUpperCase())
    .trim()
}

interface AutoCompleteOption {
  value: string
  label: string
  type: 'identifier' | 'term'
}

type AutoCompleteContextType = 'open' | 'term' | 'closed'

interface AutoCompleteContext {
  word: string
  identifier: string
  isQuote: boolean
  context: AutoCompleteContextType
}

type AutoCompleteByField = Partial<Record<SearchableProjectField, string[]>>

export const getAutoCompleteContext = (
  query: string,
  caretPosition: number,
): AutoCompleteContext => {
  const subQuery = query.slice(0, caretPosition)

  if (!subQuery) {
    return {
      word: '',
      identifier: '',
      isQuote: false,
      context: 'open',
    }
  }

  const comparison = ['<', '>']
  const assertions = [...comparison, ':', '=']
  const specialChars = [...assertions, '(', ')', '"', "'"]

  let context: AutoCompleteContextType = 'open'

  let word = ''
  let identifier = ''

  let isDoubleQuote = false
  let isSingleQuote = false
  let isEscape = false
  const parenStack: string[] = []

  for (let i = 0; i < subQuery.length; i++) {
    const char = subQuery[i]
    const isQuote = isDoubleQuote || isSingleQuote

    if (char === undefined) {
      continue
    }

    if (char === '\\') {
      isEscape = true
      continue
    }

    if (isEscape) {
      word = `${word}${char}`
      isEscape = false
      continue
    }

    if (char === '"' || char === "'") {
      // If the quote was just opened
      if (!word && !isQuote && context !== 'closed') {
        context = 'term'
      } else {
        identifier = ''
        word = ''
        context = 'closed'
      }

      if (char === '"') {
        isDoubleQuote = !isDoubleQuote
      }

      if (char === "'") {
        isSingleQuote = !isSingleQuote
      }

      continue
    }

    if (isQuote) {
      word = `${word}${char}`
      continue
    }

    if (assertions.includes(char)) {
      identifier = word
      word = ''
      context = 'term'

      if (comparison.includes(char) && subQuery[i + 1] === '=') {
        i++
      }

      continue
    }

    if (char === '(') {
      parenStack.push(identifier)

      if (identifier) {
        context = 'term'
      } else {
        identifier = ''
        context = 'open'
      }

      word = ''
      continue
    }

    if (char === ' ') {
      const outerIdentifier = parenStack[parenStack.length - 1] || ''
      identifier = outerIdentifier
      word = ''

      if (identifier) {
        context = 'term'
      } else {
        context = 'open'
      }

      continue
    }

    if (char === ')') {
      parenStack.pop()
      identifier = ''
      word = ''
      context = 'closed'
      continue
    }

    word = `${word}${char}`
  }

  const result: AutoCompleteContext = {
    word,
    identifier,
    isQuote: isSingleQuote || isDoubleQuote,
    context,
  }

  const closedContext: AutoCompleteContext = {
    word: '',
    identifier: '',
    isQuote: false,
    context: 'closed',
  }

  const nextChar = query[caretPosition]

  if (!nextChar || nextChar === ' ' || context === 'closed') {
    return result
  }

  if (context === 'term') {
    if (!isSingleQuote && nextChar === "'") {
      return closedContext
    }

    if (!isDoubleQuote && nextChar === '"') {
      return closedContext
    }

    if (nextChar === '(') {
      return closedContext
    }
  }

  if (nextChar && !specialChars.includes(nextChar)) {
    return closedContext
  }

  return result
}

export const convertSearchableObjectsToAutoCompleteByField = (
  searchableProjects: SearchableProject[],
): AutoCompleteByField => {
  const result: AutoCompleteByField = {}

  for (const project of searchableProjects) {
    for (const field in project) {
      if (!searchableFields.includes(field as keyof SearchableProject)) {
        continue
      }

      if (!result[field]) {
        result[field] = []
      }

      if (Array.isArray(project[field])) {
        result[field].push(...project[field])
        continue
      }

      if (project[field]) {
        result[field].push(project[field])
      }
    }
  }

  for (const field in result) {
    if (result[field]) {
      result[field] = [
        ...new Set(
          result[field].map((value: string) => value.trim()).filter((value: string) => !!value),
        ),
      ]
      result[field].sort(sortFunction)
    }
  }

  return result
}

export const convertSearchableProjectsToStrings = (
  searchableProjects: SearchableProject[],
): string[] => {
  let result: string[] = []

  for (const project of searchableProjects) {
    for (const field in project) {
      if (!searchableFields.includes(field as keyof SearchableProject)) {
        continue
      }

      if (Array.isArray(project[field])) {
        result.push(...project[field])
        continue
      }

      if (project[field]) {
        result.push(project[field])
      }
    }
  }

  result = [
    ...new Set(result.map((value: string) => value.trim()).filter((value: string) => !!value)),
  ]
  result.sort(sortFunction)

  return result
}

interface ProjectCpsAutoCompleteInputProps {
  query: string
  onQueryChange: (query: string) => void
  placeholder: string
  searchableProjects: SearchableProject[]
  error?: Error | null
}

export const ProjectCpsAutoCompleteInput = (props: ProjectCpsAutoCompleteInputProps) => {
  const { query, placeholder, error, searchableProjects, onQueryChange } = props

  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [inputElement, setInputElement] = useState<HTMLInputElement | null>(null)
  const [autoCompleteContext, setAutoCompleteContext] = useState<AutoCompleteContext | null>(null)

  const theme = useEuiTheme()

  const optionStringsByField: AutoCompleteByField = useMemo(
    () => convertSearchableObjectsToAutoCompleteByField(searchableProjects),
    [searchableProjects],
  )

  const termStrings: string[] = useMemo(
    () => convertSearchableProjectsToStrings(searchableProjects),
    [searchableProjects],
  )

  const fieldOptions: AutoCompleteOption[] = useMemo(
    () =>
      searchableFields.map((field) => ({
        value: field,
        label: camelCaseToTitleCase(field),
        type: 'identifier',
      })),
    [searchableProjects],
  )

  const termOptions: AutoCompleteOption[] = useMemo(
    () =>
      convertSearchableProjectsToStrings(searchableProjects).map((value) => ({
        value,
        label: value,
        type: 'term',
      })),
    [searchableProjects],
  )

  const openOptions: AutoCompleteOption[] = useMemo(() => {
    const result: AutoCompleteOption[] = []

    for (const fieldOption of fieldOptions) {
      result.push(fieldOption)

      const termOptionsByField: AutoCompleteOption[] = (
        optionStringsByField[fieldOption.value] || []
      ).map((value: string) => ({
        value,
        label: value,
        type: 'term',
      }))

      result.push(...termOptionsByField)
    }

    return result
  }, [fieldOptions, optionStringsByField])

  const autoCompleteOptions: AutoCompleteOption[] = useMemo(() => {
    if (!autoCompleteContext || autoCompleteContext.context === 'closed') {
      return []
    }

    const { word, identifier, context } = autoCompleteContext

    if (context === 'open') {
      return openOptions.filter(({ value }) => {
        if (value === word) {
          return false
        }

        return value.toLocaleLowerCase().startsWith(word.toLocaleLowerCase())
      })
    }

    if (context === 'term') {
      const optionStrings = identifier ? optionStringsByField[identifier] : termStrings
      return (optionStrings || [])
        .filter((value: string) => {
          if (value === word) {
            return false
          }

          return value.toLocaleLowerCase().startsWith(word.toLocaleLowerCase())
        })
        .map((value: string) => ({
          value,
          label: value,
          type: 'term',
        }))
    }

    return []
  }, [
    fieldOptions,
    termOptions,
    openOptions,
    optionStringsByField,
    termStrings,
    autoCompleteContext,
  ])

  const isPopoverOpen = useMemo(() => !!autoCompleteOptions.length, [autoCompleteOptions])

  const onSelectInput = () => {
    if (!query.trim()) {
      setAutoCompleteContext({
        word: '',
        identifier: '',
        isQuote: false,
        context: 'open',
      })
    } else {
      setAutoCompleteContext(null)
    }
  }

  const closePopover = () => {
    setActiveIndex(null)
    setAutoCompleteContext(null)
  }

  const onQueryChangeInternal = (newQuery: string) => {
    onQueryChange(newQuery)

    if (typeof inputElement?.selectionStart === 'number') {
      setAutoCompleteContext(getAutoCompleteContext(newQuery, inputElement.selectionStart))
    } else {
      setAutoCompleteContext(null)
    }
  }

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
    }

    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      setAutoCompleteContext(null)
    }
  }

  const insertAutoComplete = useCallback(
    (index: number | null) => {
      if (typeof index !== 'number') {
        return false
      }

      if (typeof inputElement?.selectionStart !== 'number') {
        return false
      }

      const selectedOption = autoCompleteOptions[index]

      if (!selectedOption) {
        return false
      }

      if (!autoCompleteContext || autoCompleteContext.context === 'closed') {
        return false
      }

      const { word, isQuote } = autoCompleteContext
      const value = selectedOption.value
      const caretPosition = inputElement.selectionStart
      const insertionPosition = inputElement.selectionStart - word.length

      const queryPrefix = query.slice(0, insertionPosition)
      const querySuffix = query.slice(caretPosition)

      const valueToInsert = !isQuote && selectedOption.type === 'term' ? `"${value}"` : value

      const newQuery = `${queryPrefix}${valueToInsert}${querySuffix}`
      onQueryChange(newQuery)
      setAutoCompleteContext(null)

      const newCaret = insertionPosition + valueToInsert.length
      inputElement.selectionStart = newCaret
      inputElement.selectionEnd = newCaret

      return true
    },
    [query, autoCompleteOptions, autoCompleteContext, inputElement, onQueryChange],
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isPopoverOpen) {
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((prevActiveIndex) => {
          if (prevActiveIndex === null) {
            return 0
          }

          const newIndex = prevActiveIndex - 1

          if (newIndex < 0) {
            return autoCompleteOptions.length - 1
          }

          return newIndex
        })
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((prevActiveIndex) => {
          if (prevActiveIndex === null) {
            return 0
          }

          const newIndex = prevActiveIndex + 1

          if (newIndex > autoCompleteOptions.length - 1) {
            return 0
          }

          return newIndex
        })
      }

      if (event.key === 'Enter') {
        if (insertAutoComplete(activeIndex)) {
          event.preventDefault()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isPopoverOpen, activeIndex, autoCompleteOptions, insertAutoComplete])

  useEffect(() => {
    if (!isPopoverOpen || activeIndex === null) {
      return
    }

    const listItem = document.getElementById(`autocomplete-list-item-${activeIndex}`)
    listItem?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [activeIndex])

  useEffect(() => {
    setActiveIndex(autoCompleteOptions.length ? 0 : null)
  }, [autoCompleteOptions])

  return (
    <EuiInputPopover
      closePopover={closePopover}
      fullWidth={true}
      disableFocusTrap={true}
      panelPaddingSize='none'
      isOpen={isPopoverOpen}
      input={
        <EuiFieldSearch
          data-test-id='project-cps-searchbar-text-input'
          fullWidth={true}
          inputRef={(node) => setInputElement(node)}
          value={query}
          placeholder={placeholder}
          isInvalid={!!error}
          onChange={(e) => onQueryChangeInternal(e.target.value)}
          onFocus={onSelectInput}
          onClick={onSelectInput}
          onKeyDown={onInputKeyDown}
        />
      }
    >
      <ul css={{ maxHeight: 256, overflowY: 'auto' }}>
        {autoCompleteOptions.map(({ label, type }, index) => (
          <li
            id={`autocomplete-list-item-${index}`}
            key={index}
            css={{
              fontWeight: type === 'identifier' ? theme.euiTheme.font.weight.bold : undefined,
              padding: theme.euiTheme.size.m,
              paddingLeft: type === 'term' ? theme.euiTheme.size.xl : undefined,
              backgroundColor:
                activeIndex === index
                  ? theme.euiTheme.colors.backgroundBaseInteractiveHover
                  : undefined,
              borderBlockEnd:
                index !== autoCompleteOptions.length - 1 ? theme.euiTheme.border.thin : undefined,
              ':hover': {
                backgroundColor: theme.euiTheme.colors.backgroundBaseInteractiveHover,
                cursor: 'pointer',
              },
            }}
            onClick={() => insertAutoComplete(index)}
          >
            {label}
          </li>
        ))}
      </ul>
    </EuiInputPopover>
  )
}
